// BoardIQ API client — one function per documented endpoint.
// Same-origin /api by default; override with VITE_BACKEND_URL at build time.

const BASE = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_BACKEND_URL) || "/api";

class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// The active community for this tab, set by session.js's startSession()
// right after login (POST /communities/select) succeeds. Every request
// below carries it as an X-Community-Id header, which api.py's
// _resolve_community_id() prefers over its single-tenant fallback (the
// "last active community" pointer file) -- see session.js for why. It's
// module-level rather than threaded through every call's arguments so the
// ~30 endpoints above don't each need a communityId parameter added; one
// login sets it once and everything downstream picks it up automatically.
let activeCommunityId = null;

export function setActiveCommunityId(id) {
  activeCommunityId = id || null;
}

async function req(path, { method = "GET", json, form, signal } = {}) {
  const init = { method, signal, headers: {} };
  if (activeCommunityId) init.headers["X-Community-Id"] = activeCommunityId;
  if (json !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  }
  if (form !== undefined) init.body = form; // never set Content-Type for FormData

  const res = await fetch(BASE + path, init);
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }

  if (!res.ok) {
    const detail = (body && body.detail) || res.statusText || "Request failed";
    throw new ApiError(typeof detail === "string" ? detail : JSON.stringify(detail), res.status, body);
  }
  return body;
}

export const api = {
  base: BASE,
  ApiError,
  setActiveCommunityId,

  // 5.1 search / chat
  health: () => req("/health"),
  search: ({ query, scope = "all", topK = 8, simplify = false, knowledgeMode = "grounded", history = [] }, signal) =>
    req("/search", {
      method: "POST",
      signal,
      json: { query, top_k: topK, synthesize: true, scope, simplify, knowledge_mode: knowledgeMode, history },
    }),

  // 5.2 community / documents
  getCommunity: () => req("/community"),
  listCommunities: (q, signal) => req("/communities" + (q ? "?q=" + encodeURIComponent(q) : ""), { signal }),
  selectCommunity: (name, password, username) =>
    req("/communities/select", { method: "POST", json: { name, password, username: username || "admin" } }),
  setCommunityName: (name) => req("/community/name", { method: "POST", json: { name } }),

  uploadCcrs: (file) => {
    const form = new FormData();
    form.append("file", file);
    return req("/community/ccrs", { method: "POST", form });
  },
  uploadDocument: (file, title) => {
    const form = new FormData();
    form.append("file", file);
    if (title) form.append("title", title);
    return req("/community/documents", { method: "POST", form });
  },
  deleteDocument: (id) => req("/community/documents/" + encodeURIComponent(id), { method: "DELETE" }),
  getGraphJob: (id) => req("/graph/jobs/" + encodeURIComponent(id)),

  // resident / ResidentIQ
  getManagers: () => req("/community/managers"),
  setManagers: (managers) => req("/community/managers", { method: "POST", json: { managers } }),
  triageRequest: ({ description, history = [] }, signal) =>
    req("/resident/triage", { method: "POST", signal, json: { description, history } }),
  createResidentRequest: ({ description, category, area, answer, email, resident }) =>
    req("/resident/requests", { method: "POST", json: { description, category, area, answer, email, resident } }),
  listResidentRequests: (requesterIdentifier) =>
    req("/resident/requests" + (requesterIdentifier ? "?requester_identifier=" + encodeURIComponent(requesterIdentifier) : "")),
  getResidentRequest: (id) => req("/resident/requests/" + encodeURIComponent(id)),
  editResidentRequest: (id, { actorIdentifier, description }) =>
    req("/resident/requests/" + encodeURIComponent(id), {
      method: "PATCH",
      json: { actor_identifier: actorIdentifier, description },
    }),
  addResidentRequestComment: (id, { actorIdentifier, actorRole = "resident", text }) =>
    req("/resident/requests/" + encodeURIComponent(id) + "/comments", {
      method: "POST",
      json: { actor_identifier: actorIdentifier, actor_role: actorRole, text },
    }),

  // admin / portfolio
  listAdminCommunities: () => req("/admin/communities"),
  getAdminCommunity: (id) => req("/admin/communities/" + encodeURIComponent(id)),
  createAdminCommunity: (community) =>
    req("/admin/communities", {
      method: "POST",
      json: {
        id: community.id,
        name: community.name,
        address: community.address,
        city: community.city,
        state: community.state,
        zip: community.zip,
        units: community.units,
        status: community.status,
        plan: community.plan,
      },
    }),
  patchAdminCommunity: (id, patch) =>
    req("/admin/communities/" + encodeURIComponent(id), { method: "PATCH", json: patch }),
  adminMetrics: () => req("/admin/metrics"),
  uploadAddresses: (slug, file) => {
    const form = new FormData();
    form.append("file", file);
    return req("/admin/communities/" + encodeURIComponent(slug) + "/addresses", { method: "POST", form });
  },

  // admin / unit + resident directory (see HANDOFF-ADMIN.md §4 and
  // legalcode_search/app/community_directory.py, wired up 2026-09-21)
  listUnits: (communityId) => req("/admin/communities/" + encodeURIComponent(communityId) + "/units"),
  addUnit: (communityId, unit) =>
    req("/admin/communities/" + encodeURIComponent(communityId) + "/units", { method: "POST", json: unit }),
  removeUnit: (communityId, unitId) =>
    req("/admin/communities/" + encodeURIComponent(communityId) + "/units/" + encodeURIComponent(unitId), {
      method: "DELETE",
    }),
  importUnits: (communityId, file) => {
    const form = new FormData();
    form.append("file", file);
    return req("/admin/communities/" + encodeURIComponent(communityId) + "/units/import", { method: "POST", form });
  },
  unitsCsvUrl: (communityId) => BASE + "/admin/communities/" + encodeURIComponent(communityId) + "/units.csv",

  listAdminTickets: (communityId, { status, requesterIdentifier } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (requesterIdentifier) params.set("requester_identifier", requesterIdentifier);
    const qs = params.toString();
    return req("/admin/communities/" + encodeURIComponent(communityId) + "/tickets" + (qs ? "?" + qs : ""));
  },

  listResidents: (communityId) => req("/admin/communities/" + encodeURIComponent(communityId) + "/residents"),
  addResident: (communityId, resident) =>
    req("/admin/communities/" + encodeURIComponent(communityId) + "/residents", { method: "POST", json: resident }),
  removeResident: (communityId, residentId) =>
    req(
      "/admin/communities/" + encodeURIComponent(communityId) + "/residents/" + encodeURIComponent(residentId),
      { method: "DELETE" }
    ),
  patchResident: (communityId, residentId, patch) =>
    req("/admin/communities/" + encodeURIComponent(communityId) + "/residents/" + encodeURIComponent(residentId), {
      method: "PATCH",
      json: patch,
    }),

  importResidents: (communityId, file) => {
    const form = new FormData();
    form.append("file", file);
    return req("/admin/communities/" + encodeURIComponent(communityId) + "/residents/import", {
      method: "POST",
      form,
    });
  },
  residentsCsvUrl: (communityId) =>
    BASE + "/admin/communities/" + encodeURIComponent(communityId) + "/residents.csv",

  // Explicit placeholder, not real authentication -- see
  // community_directory.py's AuthSessionRequest docstring and the
  // integration plan's open question 4.
  authSession: (communityId, identifier) =>
    req("/auth/session", { method: "POST", json: { community_id: communityId, identifier } }),

  // resident dashboard (see legalcode_search/app/resident_dashboard.py) --
  // one round-trip for the name/email/unit header, request counts,
  // recent questions, last communication, last payment, and announcements
  // a signed-in resident sees right after login.
  getResidentDashboard: (residentId) =>
    req("/resident/dashboard?resident_id=" + encodeURIComponent(residentId)),
  logResidentQuery: (residentEmail, question) =>
    req("/resident/queries", { method: "POST", json: { resident_email: residentEmail, question } }),
  listAnnouncements: () => req("/announcements"),

  // 5.3 resolutions
  resolutionChat: ({ message, history = [] }, signal) =>
    req("/resolutions/chat", { method: "POST", signal, json: { message, history } }),
  listResolutions: (status) => req("/resolutions" + (status ? "?status=" + encodeURIComponent(status) : "")),
  getResolution: (id) => req("/resolutions/" + encodeURIComponent(id)),
  createResolution: ({ title, text, createdBy }) =>
    req("/resolutions", { method: "POST", json: { title, text, created_by: createdBy } }),
  patchResolution: (id, { title, text, actor }) =>
    req("/resolutions/" + encodeURIComponent(id), { method: "PATCH", json: { title, text, actor } }),
  approveResolution: (id, signerName) =>
    req("/resolutions/" + encodeURIComponent(id) + "/approve", { method: "POST", json: { signer_name: signerName } }),
  deleteResolution: (id) => req("/resolutions/" + encodeURIComponent(id), { method: "DELETE" }),
  searchResolutions: ({ query, topK = 10 }) =>
    req("/resolutions/search", { method: "POST", json: { query, top_k: topK } }),
};

// Password convention, computed client-side for display only (verified server-side).
export const derivePassword = (name) => "password-" + String(name || "").trim().toLowerCase().replace(/\s+/g, "");

// Poll a graph ingestion job until it settles. onTick receives each job payload.
export async function pollGraphJob(id, onTick, { intervalMs = 1200, timeoutMs = 180000 } = {}) {
  const started = Date.now();
  for (;;) {
    const job = await api.getGraphJob(id);
    onTick && onTick(job);
    if (job.status === "completed" || job.status === "failed") return job;
    if (Date.now() - started > timeoutMs) return { ...job, status: "failed", error: "Client timeout" };
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
